import { z } from 'zod';
import { MAX_FIELD_CHOICES, fieldIdSchema } from '../intake.js';
import { KEY_RE } from './keys.js';
/**
 * The one-profile vocabulary as types, one place (spec 1.1). Enumerations are `as const` arrays plus zod
 * enums; the database CHECKs on these columns use a slug pattern only, so adding a value never needs a
 * migration.
 */
export const SECTION_KEYS = [
    'basics', 'look', 'skills', 'work', 'media', 'availability', 'commercial', 'boundaries', 'health',
    'family', 'documents', 'money', 'personal', 'social', 'voiceover', 'modeling', 'agency', 'logins',
];
export const QUESTION_TYPES = [
    'short_text', 'long_text', 'number', 'measurement', 'date', 'email', 'phone', 'url', 'address',
    'select', 'multi_select', 'matrix', 'yes_no', 'file', 'contact',
];
export const SENSITIVITIES = ['standard', 'personal', 'sensitive'];
/** Who answers and who sees. */
export const QUESTION_OWNERS = ['talent', 'per_agency', 'agency'];
export const QUESTION_AUDIENCES = ['everyone', 'adults', 'minors'];
/** PURE: a question of this audience is for these people: it is for everyone (or names no audience), or for them. */
export const audienceIncludes = (audience, who) => !audience || audience === 'everyone' || audience === who;
export const WORDING_AUDIENCES = ['any', 'adult', 'guardian', 'agency'];
/** answer: talent_answers/agency_answers. core: a clients column. secure: encrypted per agency in
 *  profile_secure, never in the answer tables (Phases 1 to 6). vault: credential vault item (Phase 4).
 *  performer: the person-level child_performer_profiles row. photo, document: their own pipelines. */
export const STORAGE_KINDS = ['answer', 'core', 'secure', 'vault', 'performer', 'photo', 'document'];
export const AGENCY_ORIGINS = ['agency', 'talent', 'legacy', 'snapshot'];
export const ANSWER_SOURCES = [
    'talent_edit', 'intake', 'accepted_suggestion', 'approved_ask', 'claim', 'quick_check',
    'legacy_portable', 'legacy_history', 'legacy_row', 'legacy_performer',
    // Phase 6 (0099 library_undo_decision): an answer given under a library key after a decision, copied back to the
    // agency's own key when the decision is undone.
    'library_undo',
];
export const REVIEW_REASONS = ['guardian_portable', 'matches_guardian', 'unknown_provenance'];
export const GRANT_VIA = [
    'legacy', 'mirror', 'intake', 'intake_undisclosed', 'approval', 'accepted_suggestion', 'talent_push',
    'vault_share', 'alias',
];
export const GRANT_END_REASONS = ['revoked', 'left', 'erased', 'relinked', 'rejected', 'unshared', 'merge_undone'];
export const SUGGESTION_STATUSES = ['open', 'accepted', 'dismissed', 'superseded', 'replaced', 'withdrawn'];
export const ASK_STATUSES = ['open', 'approved', 'declined', 'withdrawn', 'answered'];
/** The identity kinds a vault-stored question names (Phase 4 widens the vault item kinds around them). */
export const QUESTION_VAULT_KINDS = ['ssn', 'bank', 'ein', 'permit'];
export const sectionKeySchema = z.enum(SECTION_KEYS);
export const questionTypeSchema = z.enum(QUESTION_TYPES);
export const sensitivitySchema = z.enum(SENSITIVITIES);
export const questionOwnerSchema = z.enum(QUESTION_OWNERS);
export const questionAudienceSchema = z.enum(QUESTION_AUDIENCES);
export const wordingAudienceSchema = z.enum(WORDING_AUDIENCES);
export const storageKindSchema = z.enum(STORAGE_KINDS);
export const agencyOriginSchema = z.enum(AGENCY_ORIGINS);
export const answerSourceSchema = z.enum(ANSWER_SOURCES);
export const reviewReasonSchema = z.enum(REVIEW_REASONS);
export const grantViaSchema = z.enum(GRANT_VIA);
export const grantEndReasonSchema = z.enum(GRANT_END_REASONS);
export const suggestionStatusSchema = z.enum(SUGGESTION_STATUSES);
export const askStatusSchema = z.enum(ASK_STATUSES);
/** Question types whose answers are picked from `options`. */
export const CHOICE_QUESTION_TYPES = ['select', 'multi_select', 'matrix'];
const text = (max) => z.string().trim().min(1).max(max);
/** A choice list or a grid's rows: the same bound as an intake field's (intake.ts MAX_FIELD_CHOICES), so every question
 *  is a valid field. The longest real list is 286 (heritages); the longest grid has 227 rows and 8 columns. */
const choiceList = z.array(text(120)).max(MAX_FIELD_CHOICES);
/** Validates code data AND agency_questions.def (one schema for both shelves). */
export const libraryQuestionSchema = z
    .object({
    key: z.string().regex(KEY_RE, 'key must be <section>.<snake>'),
    label: text(200),
    section: sectionKeySchema,
    subsection: text(80),
    type: questionTypeSchema,
    options: choiceList.optional(),
    columns: choiceList.optional(),
    option_aliases: z.record(text(120), text(120)).optional(),
    unit: text(120).optional(),
    ways_to_ask: z.array(z.object({ wording: text(300), audience: wordingAudienceSchema, help: z.string().max(1000).optional() })),
    search_phrases: z.array(text(200)),
    sensitivity: sensitivitySchema,
    owner: questionOwnerSchema,
    audience: questionAudienceSchema,
    freshness_days: z.number().int().positive().nullable(),
    purpose: z.string().max(1000),
    legacy_keys: z.array(fieldIdSchema),
    sources: z.array(z.string().max(300)).optional(),
    storage: storageKindSchema,
    vault_kind: z.enum(QUESTION_VAULT_KINDS).nullable().optional(),
    performer_column: z.string().regex(/^[a-z_]{1,64}$/).nullable().optional(),
})
    .superRefine((q, ctx) => {
    if (CHOICE_QUESTION_TYPES.includes(q.type) && !q.options?.length)
        ctx.addIssue({ code: 'custom', path: ['options'], message: `a ${q.type} question needs options` });
    if (q.type === 'matrix' && !q.columns?.length)
        ctx.addIssue({ code: 'custom', path: ['columns'], message: 'a matrix question needs columns' });
    for (const [alias, option] of Object.entries(q.option_aliases ?? {})) {
        if (!CHOICE_QUESTION_TYPES.includes(q.type) || !q.options?.includes(option) || q.options.includes(alias))
            ctx.addIssue({ code: 'custom', path: ['option_aliases', alias], message: 'an option alias names another wording of one of its options' });
    }
    if (q.storage === 'vault' && !q.vault_kind)
        ctx.addIssue({ code: 'custom', path: ['vault_kind'], message: 'a vault question names its vault kind' });
    if (q.storage === 'performer' && !q.performer_column)
        ctx.addIssue({ code: 'custom', path: ['performer_column'], message: 'a performer question names its column' });
    if (q.sensitivity === 'sensitive' && q.storage === 'answer')
        ctx.addIssue({ code: 'custom', path: ['storage'], message: 'a sensitive answer is never stored in the answer tables' });
});
/** The column names of ClientTalentColumns, in a fixed order (diffs and reports list them this way). */
export const CLIENT_TALENT_COLUMNS = ['full_name', 'email', 'phone', 'extra_phones', 'headshot_url', 'profile'];
/** full_name '', nulls, [], {}: the parity seed (spec 3.7). Frozen: callers copy, never mutate. */
export const EMPTY_COLUMNS = Object.freeze({
    full_name: '',
    email: null,
    phone: null,
    extra_phones: Object.freeze([]),
    headshot_url: null,
    profile: Object.freeze({}),
});
//# sourceMappingURL=types.js.map